import React, { useState, useEffect, useCallback, useMemo } from 'react';
import i18n from 'i18next';
import styles from './shopForm.module.scss';
import { get } from 'lodash';
import { connect, FormikContextType } from 'formik';
import { validateEmpty } from 'utils/ValidateUtils';
import { ShopFormProps } from './ShopFromModel';
import { FormConfig } from 'components/form/FormConfig';
import { FormContent } from 'components/form/Form';
import { useSelectProductField } from './useSelectProductField';
import { useCallAPI } from 'hooks/useCallAPI';
import { DefaultBrandManager } from 'core/brand/BrandManager';
import { userStore } from 'store/user';
import { useCampaignStore } from 'store/campaignStore';
import { useProductStore } from 'store/productStore';

const defaultBrandManager = new DefaultBrandManager();

export const ShopForm: React.FunctionComponent<
  ShopFormProps & { formik: FormikContextType<any> }
> = (props) => {
  const { renderHintModal, getInitFormConfig } = props;
  const { selectedProducts: products } = useProductStore();
  const { handleSelectBrand, resetCampaignStore } = useCampaignStore();
  const { flowPageModel, removeSelectProduct } = props.model;

  const { seller } = userStore.getState();
  const { loading, callAPIs } = useCallAPI();
  const { values, setFieldValue } = props.formik;

  const [formConfig, setFormConfig] = useState<FormConfig>(getInitFormConfig());
  const [brandOptions, setBrandOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [cnCodeOptions, setCnCodeOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [cnCodeLoading, setCnCodeLoading] = useState<boolean>(false);
  const [hasFetchedCnCode, setHasFetchedCnCode] = useState<boolean>(false);

  const primaryProductId = get(
    flowPageModel.initCreative.basic.typeProperties.primaryProduct,
    'productId',
  );
  const advertiserId = values.advertiserId;
  const { selectProductsFieldContainerClass, renderProductsField } =
    useSelectProductField(
      advertiserId,
      props.model,
      true,
      false,
      values.brand === '' || values.brand === undefined,
      1, // availableProducts
      false, // showImportButton
    );
  const fetchBrandOptions = useCallback(async (): Promise<void> => {
    try {
      await callAPIs(
        [defaultBrandManager.getBrandOptions.bind(defaultBrandManager)],
        (brandOptions) => {
          setBrandOptions(brandOptions);
        },
      );
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  }, [callAPIs]);

  const fetchCnCodeOptions = useCallback(
    async (webBrandNo: string): Promise<void> => {
      if (hasFetchedCnCode || flowPageModel.type === 'edit') return;

      setCnCodeLoading(true);
      try {
        const cnCodeOptions =
          await defaultBrandManager.getCnCodeOptions(webBrandNo);
        setCnCodeOptions(cnCodeOptions);

        if (cnCodeOptions.length > 0 && !values.cnCode) {
          setFieldValue('cnCode', cnCodeOptions[0]);
        }
        setHasFetchedCnCode(true);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setCnCodeLoading(false);
      }
    },
    [setFieldValue, values.cnCode, hasFetchedCnCode, flowPageModel.type],
  );

  const hideCnCode = useMemo(() => {
    if (seller !== '1P') return true;
    if (flowPageModel.type === 'edit') return !values.cnCode.label;
    return cnCodeOptions.length === 0;
  }, [seller, flowPageModel.type, values?.cnCode?.label, cnCodeOptions.length]);

  const hideUrl = useMemo(() => {
    if (seller === '3P') return true;
    if (seller === '1P') {
      if (flowPageModel.type === 'edit') return values.cnCode?.label;
      return !values.brand || cnCodeOptions.length > 0;
    }
    return true;
  }, [
    seller,
    flowPageModel.type,
    values?.cnCode?.label,
    values.brand,
    cnCodeOptions.length,
  ]);

  const onChangeBrand = useCallback(() => {
    removeSelectProduct();
    if (seller === '1P' && flowPageModel.type !== 'edit') {
      if (values.cnCode) {
        setFieldValue('cnCode', '');
      }
      if (values.bannerUrl) {
        setFieldValue('bannerUrl', '');
      }
      setHasFetchedCnCode(false);
    }
  }, [
    removeSelectProduct,
    seller,
    setFieldValue,
    flowPageModel.type,
    values.cnCode,
    values.bannerUrl,
  ]);

  const validateBannerUrl = useCallback((url: string) => {
    return url
      ? url.match(/cn=([0-9]{10,})(?=&|\b)/)
        ? ''
        : i18n.t<string>('formValidate.labels.urlNeedCn')
      : validateEmpty(url);
  }, []);

  useEffect(() => {
    if (flowPageModel.type === 'create') {
      fetchBrandOptions();
    }
  }, [fetchBrandOptions, flowPageModel.type]);

  useEffect(() => {
    if (values.brand && seller === '1P' && flowPageModel.type !== 'edit') {
      fetchCnCodeOptions(values.brand.value);
    }
  }, [values.brand, seller, fetchCnCodeOptions, flowPageModel.type]);

  useEffect(() => {
    handleSelectBrand(values.brand);
  }, [values.brand]);

  useEffect(() => {
    return () => {
      setFieldValue('brand', '');
      setFieldValue('cnCode', '');
      setFieldValue('bannerUrl', '');
    };
  }, []);

  useEffect(() => {
    if (primaryProductId === undefined && products.length > 0) {
      setFieldValue('typeProperties.primaryProduct', products[0]);
    }
    setFormConfig(
      new FormConfig.Builder()
        .addSection(
          new FormConfig.SectionBuilder(
            new FormConfig.FieldsBuilder([...props.basicFields])
              .addFormikSelect({
                label: i18n.t<string>('creativeSetupFlow.labels.brand'),
                name: 'brand',
                simpleValue: false,
                options: brandOptions,
                onChange: onChangeBrand,
                validate: validateEmpty,
                disabled:
                  flowPageModel.type === 'edit' || brandOptions.length === 0,
                className: loading ? 'loading-select' : '',
                placeholder: loading
                  ? i18n.t<string>('common.messages.loading-field')
                  : i18n.t<string>('common.placeholder.pleaseSelect'),
                hoverHint:
                  brandOptions.length === 0
                    ? i18n.t<string>('creativeSetupFlow.hints.noBrand')
                    : '',
                hint: i18n.t<string>('creativeSetupFlow.hints.brand'),
              })
              .addFormikSelect(
                {
                  label: i18n.t<string>('creativeSetupFlow.labels.cnCode'),
                  name: 'cnCode',
                  simpleValue: false,
                  options: cnCodeOptions,
                  validate: validateEmpty,
                  disabled:
                    flowPageModel.type === 'edit' || cnCodeOptions.length === 1,
                  className: cnCodeLoading ? 'loading-select' : '',
                  placeholder: cnCodeLoading
                    ? i18n.t<string>('common.messages.loading-field')
                    : i18n.t<string>('common.placeholder.pleaseSelect'),
                },
                hideCnCode,
              )
              .addFormikUrlInput(
                {
                  label: i18n.t<string>('creativeSetupFlow.labels.bannerUrl'),
                  name: 'bannerUrl',
                  validate: validateBannerUrl,
                  onChange(value) {
                    const trimValue = value.replace(/\s/g, '');
                    setFieldValue('bannerUrl', trimValue);
                    if (hideCnCode && trimValue.includes('cn=')) {
                      const cnCodeMatch = trimValue.match(
                        /cn=([0-9]{10,})(?=&|\b)/,
                      );
                      if (cnCodeMatch) {
                        setFieldValue('cnCode', {
                          label: '',
                          value: cnCodeMatch[1],
                        });
                      }
                    }
                  },
                  className: styles.bannerUrlInput,
                  hint: (
                    <p>
                      此欄位需輸入小網館架網址，您可將下方網址中
                      <span className={styles.red}>
                        紅色數字置換成您的館架ID
                      </span>
                      後，貼上使用
                      <br />
                      https://m.momoshop.com.tw/category.momo?cn=
                      <span className={styles.red}>1234567890</span>
                      &cid=dir&oid=product
                      <br />
                      請務必點選開啟連結按鈕確認網址是否正確
                    </p>
                  ),
                },
                hideUrl,
              )
              .addFormikCustom(
                {
                  name: 'typeProperties.primaryProduct',
                  label: i18n.t<string>(
                    'creativeSetupFlow.labels.primaryProduct',
                  ),
                  hint: i18n.t<string>(
                    'creativeSetupFlow.hints.primaryProduct',
                  ),
                  render: renderProductsField,
                  formGroupClassName: selectProductsFieldContainerClass,
                },
                primaryProductId !== undefined,
              )
              .addFormikLabel(
                {
                  name: 'typeProperties.primaryProduct.name',
                  label: i18n.t<string>(
                    'creativeSetupFlow.labels.primaryProduct',
                  ),
                },
                primaryProductId === undefined,
              )
              .build(),
          )
            .withTitle(
              i18n.t<string>('creativeSetupFlow.labels.creativeBasicInfo'),
            )
            .withHintModal(renderHintModal())
            .build(),
        )
        .build(),
    );
  }, [
    flowPageModel.type,
    products,
    props.basicFields,
    selectProductsFieldContainerClass,
    primaryProductId,
    setFieldValue,
    renderProductsField,
    renderHintModal,
    onChangeBrand,
    brandOptions,
    loading,
    seller,
    cnCodeOptions,
    cnCodeLoading,
    values,
    hideCnCode,
    hideUrl,
    validateBannerUrl,
  ]);

  return <FormContent formConfig={formConfig} />;
};

export default connect(ShopForm);
